import { Link, useParams } from 'react-router-dom';
import { IconAlertCircle, IconArrowLeft } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '../api.js';

type Tone = 'green' | 'blue' | 'red' | 'orange' | 'neutral';

export function ChangeDetailPage() {
  const { n } = useParams<{ n: string }>();
  const change = Number.parseInt(n ?? '', 10);

  const { data, isLoading, error } = useQuery({
    queryKey: ['change', change],
    queryFn: () => api.changeDetail(change),
    enabled: Number.isFinite(change) && change > 0,
  });

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-4">
      <Link
        to="/changes"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <IconArrowLeft size={14} />
        All changes
      </Link>

      {isLoading && (
        <div className="flex items-center gap-2">
          <Spinner />
          <span className="text-sm text-muted-foreground">Loading change…</span>
        </div>
      )}

      {error && (
        <Alert tone="destructive">
          <IconAlertCircle />
          <AlertTitle>Failed to load change</AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      )}

      {data && (
        <>
          <div className="flex items-baseline gap-2 flex-wrap">
            <h1 className="font-mono text-2xl font-semibold tracking-tight">
              @{data.change}
            </h1>
            <Badge>{data.status ?? 'unknown'}</Badge>
            <span className="text-xs text-muted-foreground">
              by <span className="font-mono">{data.user ?? '?'}</span>{' '}
              on <span className="font-mono">{data.client ?? '?'}</span>
            </span>
          </div>

          <Card>
            <CardContent className="py-4">
              <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">
                {data.description || (
                  <span className="text-muted-foreground italic">
                    (no description)
                  </span>
                )}
              </pre>
            </CardContent>
          </Card>

          <h2 className="mt-2 text-base font-semibold">
            Files ({data.files.length})
          </h2>
          <div className="rounded-md border border-neutral-800 bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Action</TableHead>
                  <TableHead className="w-[100px]">Type</TableHead>
                  <TableHead className="w-[80px]">Rev</TableHead>
                  <TableHead>File</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.files.map((f) => (
                  <TableRow key={f.depotFile}>
                    <TableCell>
                      <Badge tone={actionTone(f.action)}>{f.action ?? '—'}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-[11px] text-muted-foreground">
                      {f.type ?? '—'}
                    </TableCell>
                    <TableCell className="font-mono">#{f.rev ?? '—'}</TableCell>
                    <TableCell className="font-mono">
                      <Link
                        to={modalHrefFor(f.depotFile)}
                        className="hover:text-theia-400 hover:underline"
                      >
                        {f.depotFile}
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}

function actionTone(action: string | null): Tone {
  switch (action) {
    case 'add':
    case 'branch':
    case 'move/add':
      return 'green';
    case 'edit':
    case 'integrate':
      return 'blue';
    case 'delete':
    case 'move/delete':
      return 'red';
    default:
      return 'neutral';
  }
}

function depotPathToUrl(depotPath: string): string {
  // //depot/foo/bar.txt -> /depot/foo/bar.txt under the Depot router
  return depotPath.replace(/^\/\//, '/');
}

/**
 * Builds a Depot URL that opens the file in the same modal overlay
 * used by clicks from the directory grid (`?file=<path>` query param).
 * The route pathname points at the file's parent directory so closing
 * the modal lands the user at a familiar listing instead of the
 * file-detail page.
 */
function modalHrefFor(depotFile: string): string {
  const lastSlash = depotFile.lastIndexOf('/');
  const parentDir = lastSlash > 1 ? depotFile.slice(0, lastSlash) : depotFile;
  return `/depot${depotPathToUrl(parentDir)}?file=${encodeURIComponent(depotFile)}`;
}
